import { useState } from 'react';
import type { ReactNode } from 'react';
import { Box, IconButton, Typography } from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import theme from '../../theme/theme';
import { DetailCard } from './DetailCard';
import { FieldGrid } from './FieldGrid';
import type { VisibleField } from './FieldGrid';

type Props<T> = {
  items: T[];
  resolveFields: (item: T, t: (k: string) => string) => VisibleField[];
  icon: ReactNode;
  iconBg: string;
  title: string;
  t: (k: string) => string;
  /** Translated label for the "previous" button (aria-label + title) */
  previousLabel: string;
  /** Translated label for the "next" button (aria-label + title) */
  nextLabel: string;
  stack?: boolean;
  stretch?: boolean;
  /** Fold the card body behind a header chevron toggle. */
  collapsible?: boolean;
  /** Initial expanded state when {@link collapsible}. Defaults to expanded. */
  defaultExpanded?: boolean;
};

export function PaginatedDetailCard<T>({
  items,
  resolveFields,
  icon,
  iconBg,
  title,
  t,
  previousLabel,
  nextLabel,
  stack,
  stretch,
  collapsible,
  defaultExpanded,
}: Props<T>) {
  const [index, setIndex] = useState(0);

  const safeItems = Array.isArray(items) ? items : [];
  const count = safeItems.length;
  const safeIndex = Math.min(index, Math.max(0, count - 1));
  const hasMany = count > 1;

  const pagination = hasMany ? (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
      <IconButton
        size="small"
        onClick={() => setIndex(i => i - 1)}
        disabled={safeIndex === 0}
        aria-label={previousLabel}
        title={previousLabel}
      >
        <ChevronLeftIcon fontSize="small" />
      </IconButton>
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ minWidth: theme.spacing(5), textAlign: 'center' }}
      >
        {safeIndex + 1} / {count}
      </Typography>
      <IconButton
        size="small"
        onClick={() => setIndex(i => i + 1)}
        disabled={safeIndex === count - 1}
        aria-label={nextLabel}
        title={nextLabel}
      >
        <ChevronRightIcon fontSize="small" />
      </IconButton>
    </Box>
  ) : undefined;

  return (
    <DetailCard
      stretch={stretch}
      icon={icon}
      iconBg={iconBg}
      title={title}
      headerExtra={pagination}
      collapsible={collapsible}
      defaultExpanded={defaultExpanded}
    >
      <FieldGrid fields={resolveFields((safeItems[safeIndex] ?? {}) as T, t)} stack={stack} />
    </DetailCard>
  );
}
